import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from '../../components/toasts';
import { TeamBadge } from '../../components/TeamBadge';
import { Badge, Button, ErrorText, Field, Input, Modal, Spinner, Textarea } from '../../components/ui';
import { get, patch, post } from '../../lib/api';
import { ChannelAccount, Team, UserView } from '../../lib/types';

/** Equipes: membros, números atendidos, ordem no menu e mensagens próprias de início e encerramento. */
export function TeamsAdmin() {
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => get<Team[]>('/teams') });
  const users = useQuery({ queryKey: ['users'], queryFn: () => get<UserView[]>('/users') });
  const channels = useQuery({ queryKey: ['channels'], queryFn: () => get<ChannelAccount[]>('/channels') });
  const [editing, setEditing] = useState<Team | 'new' | null>(null);
  const userName = (id: string) => users.data?.find((u) => u.id === id)?.name ?? '?';
  const channelName = (id: string) => channels.data?.find((c) => c.id === id)?.name ?? '?';

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="max-w-3xl text-sm text-fg-soft">
          O operador vê na fila os atendimentos das equipes dele (e os sem equipe). Número com uma equipe: o atendimento já cai nela. Com mais de uma: o cliente escolhe num menu (texto em Configurações → <code>bot.menu_message</code>).
        </p>
        <Button variant="primary" onClick={() => setEditing('new')}>
          <Plus className="size-4" /> Nova equipe
        </Button>
      </div>
      {teams.isLoading && <Spinner />}
      <ErrorText error={teams.error} />
      {teams.data?.length === 0 && <p className="text-sm text-fg-soft">Nenhuma equipe cadastrada: todos os atendimentos vão para a fila geral.</p>}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(teams.data ?? []).map((t) => (
          <div key={t.id} className={`rounded-lg border border-border bg-panel p-4 ${t.active ? '' : 'opacity-60'}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <TeamBadge name={t.name} size="md" /> {!t.active && <Badge>inativa</Badge>}
                </div>
                <div className="text-xs text-fg-soft">
                  no menu: &quot;{t.menuLabel || t.name}&quot; · ordem {t.sortOrder}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setEditing(t)}>
                <Pencil className="size-3.5" /> Editar
              </Button>
            </div>
            {t.description && <p className="mt-1 text-sm text-fg-soft">{t.description}</p>}
            <div className="mt-2 text-xs">
              <span className="text-fg-soft">Números: </span>
              {t.channelIds.length ? t.channelIds.map(channelName).join(', ') : <span className="text-warn">nenhum (não recebe atendimentos)</span>}
            </div>
            <div className="mt-1 text-xs">
              <span className="text-fg-soft">Membros: </span>
              {t.memberIds.length ? t.memberIds.map(userName).join(', ') : <span className="text-fg-soft">nenhum (todos os operadores veem a fila)</span>}
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              <Badge tone={t.welcomeMessage ? 'accent' : 'neutral'}>{t.welcomeMessage ? 'mensagem inicial própria' : 'mensagem inicial geral'}</Badge>
              <Badge tone={t.closingMessage ? 'accent' : 'neutral'}>{t.closingMessage ? 'encerramento próprio' : 'encerramento geral'}</Badge>
            </div>
          </div>
        ))}
      </div>
      {editing && <TeamModal team={editing === 'new' ? null : editing} users={users.data ?? []} channels={channels.data ?? []} onClose={() => setEditing(null)} />}
    </div>
  );
}

function TeamModal({ team, users, channels, onClose }: { team: Team | null; users: UserView[]; channels: ChannelAccount[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: team?.name ?? '',
    description: team?.description ?? '',
    menuLabel: team?.menuLabel ?? '',
    sortOrder: team?.sortOrder ?? 0,
    welcomeMessage: team?.welcomeMessage ?? '',
    closingMessage: team?.closingMessage ?? '',
    active: team?.active ?? true,
    memberIds: team?.memberIds ?? [],
    channelIds: team?.channelIds ?? [],
  });
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k: 'memberIds' | 'channelIds', id: string, on: boolean) => set(k, on ? [...form[k], id] : form[k].filter((x) => x !== id));

  const save = async () => {
    setSaving(true);
    setError(null);
    const body = {
      ...form,
      name: form.name.trim(),
      description: form.description.trim() || null,
      menuLabel: form.menuLabel.trim() || null,
      welcomeMessage: form.welcomeMessage.trim() || null,
      closingMessage: form.closingMessage.trim() || null,
      sortOrder: Number(form.sortOrder) || 0,
    };
    try {
      if (team) await patch(`/teams/${team.id}`, body);
      else await post('/teams', body);
      void qc.invalidateQueries({ queryKey: ['teams'] });
      toast(team ? 'Equipe atualizada' : 'Equipe criada', 'ok');
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={team ? `Equipe: ${team.name}` : 'Nova equipe'}
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="primary" loading={saving} disabled={!form.name.trim()} onClick={() => void save()}>
            Salvar
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-[1fr_1fr_6rem] gap-3">
        <Field label="Nome">
          <Input value={form.name} onChange={(e) => set('name', e.target.value)} maxLength={80} autoFocus />
        </Field>
        <Field label="Texto no menu" hint="(vazio = nome)">
          <Input value={form.menuLabel} onChange={(e) => set('menuLabel', e.target.value)} maxLength={80} placeholder={form.name || 'Ex.: Suporte técnico'} />
        </Field>
        <Field label="Ordem">
          <Input type="number" value={form.sortOrder} onChange={(e) => set('sortOrder', Number(e.target.value))} />
        </Field>
      </div>
      <Field label="Descrição" hint="(opcional, só para a equipe interna)">
        <Input value={form.description} onChange={(e) => set('description', e.target.value)} maxLength={500} />
      </Field>
      <label className="mb-3 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
        Ativa (aparece no menu e recebe atendimentos)
      </label>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Números atendidos">
          <div className="max-h-40 overflow-y-auto rounded-md border border-border p-2">
            {channels.length === 0 && <p className="text-xs text-fg-soft">Nenhum número cadastrado.</p>}
            {channels.map((c) => (
              <label key={c.id} className="flex items-center gap-2 py-0.5 text-sm">
                <input type="checkbox" checked={form.channelIds.includes(c.id)} onChange={(e) => toggle('channelIds', c.id, e.target.checked)} />
                {c.name}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Membros" hint="(veem a fila desta equipe)">
          <div className="max-h-40 overflow-y-auto rounded-md border border-border p-2">
            {users.filter((u) => u.active).map((u) => (
              <label key={u.id} className="flex items-center gap-2 py-0.5 text-sm">
                <input type="checkbox" checked={form.memberIds.includes(u.id)} onChange={(e) => toggle('memberIds', u.id, e.target.checked)} />
                {u.name}
              </label>
            ))}
          </div>
        </Field>
      </div>

      <Field label="Mensagem inicial" hint="(enviada quando o atendimento cai nesta equipe; vazio = mensagem geral)">
        <Textarea rows={3} value={form.welcomeMessage} onChange={(e) => set('welcomeMessage', e.target.value)} maxLength={2000} placeholder="Ex.: Olá! Aqui é o Suporte. Em instantes um técnico vai falar com você." />
      </Field>
      <Field label="Mensagem de encerramento" hint="(ao finalizar; vazio = mensagem geral)">
        <Textarea rows={3} value={form.closingMessage} onChange={(e) => set('closingMessage', e.target.value)} maxLength={2000} placeholder="Ex.: Atendimento do Suporte encerrado. Obrigado!" />
      </Field>
      <ErrorText error={error} />
    </Modal>
  );
}
